import { env } from '@/config/env';
import { CATEGORY, CLASS_SCOPE_LABEL } from '../constants';
import { addDays } from './dates';
import type { CalendarEventItem } from '../types/calendar';

/**
 * The academic year as an iCalendar file.
 *
 * Google Calendar, Outlook and Apple Calendar all import `.ics`, so one file
 * covers "sync with my calendar" for every parent and teacher without the
 * product holding anyone's calendar credentials.
 */

/** `2026-08-15` → `20260815`. */
function stampDate(iso: string): string {
  return iso.replace(/-/g, '');
}

/** `2026-08-15` + `09:00` → `20260815T090000` — local, floating time. */
function stampDateTime(iso: string, time: string): string {
  return `${stampDate(iso)}T${time.replace(':', '')}00`;
}

/** Commas, semicolons, backslashes and newlines all carry meaning in a line. */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

/**
 * RFC 5545 allows 75 octets to a line; longer ones continue on the next line
 * after a single space. Outlook rejects a file that ignores this.
 */
function fold(line: string): string {
  if (line.length <= 75) return line;

  const parts: string[] = [line.slice(0, 75)];
  for (let index = 75; index < line.length; index += 74) {
    parts.push(` ${line.slice(index, index + 74)}`);
  }
  return parts.join('\r\n');
}

export function buildCalendarFile(events: CalendarEventItem[], sessionLabel: string): string {
  // A UTC stamp for when the file was written; every VEVENT needs one.
  const stamp = `${new Date().toISOString().replace(/[-:]/g, '').slice(0, 15)}Z`;

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    `PRODID:-//${escapeText(env.appName)}//Academic calendar//EN`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeText(sessionLabel)}`,
  ];

  for (const item of events) {
    const style = CATEGORY[item.category];
    const details = [item.description, CLASS_SCOPE_LABEL[item.classScope]]
      .filter(Boolean)
      .join(' — ');

    lines.push(
      'BEGIN:VEVENT',
      `UID:${item.uuid}@academic-calendar`,
      `DTSTAMP:${stamp}`,
      `SUMMARY:${escapeText(item.title)}`,
      `DESCRIPTION:${escapeText(details)}`,
      `CATEGORIES:${escapeText(style.label)}`,
    );

    if (item.startTime) {
      lines.push(
        `DTSTART:${stampDateTime(item.startDate, item.startTime)}`,
        `DTEND:${stampDateTime(item.endDate, item.endTime ?? item.startTime)}`,
      );
    } else {
      // An all-day VEVENT ends on the morning after its last day: DTEND is
      // exclusive, so a one-day holiday written as start = end imports as an
      // event of no length and shows on nobody's calendar.
      lines.push(
        `DTSTART;VALUE=DATE:${stampDate(item.startDate)}`,
        `DTEND;VALUE=DATE:${stampDate(addDays(item.endDate, 1))}`,
      );
    }

    lines.push('END:VEVENT');
  }

  lines.push('END:VCALENDAR');

  return `${lines.map(fold).join('\r\n')}\r\n`;
}

/** Hands the file to the browser under a name that says what session it is. */
export function downloadCalendarFile(contents: string, fileName: string): void {
  const blob = new Blob([contents], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();

  // Revoked on the next tick rather than immediately: Safari has not finished
  // reading the blob when `click()` returns.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
